import type { SlotScreenId, ScreenConfig, VideoCompilationConfig, ScreenGenerationSettings, VideoSlotImage, VideoResolutionPreset } from '@core/types/slot-screen-generator.types';
import { SLOT_SCREEN_ORDER } from '@core/types/slot-screen-generator.types';

export const DEFAULT_SYSTEM_PROMPT_JSON = JSON.stringify({
  meta: {
    recommended_output_resolution: '720x1280',
    aspect_ratio: '9:16',
    format: 'portrait',
  },
  global_style: {
    genre: '',
    camera: { angle: 'Straight-on perspective', lens: 'Standard perspective' },
    lighting: { key_light: '', shadows: '' },
  },
  art_direction: '',
  color_palette: {
    dominant: [],
    accent_warm: [],
    accent_cool: [],
    highlight: [],
    overall_saturation: 'high',
  },
  background: {
    description: '',
    elements: [],
    depth_of_field: 'Shallow',
  },
}, null, 2);

// 6 screen default configs
export const DEFAULT_SCREEN_CONFIGS: Record<SlotScreenId, Omit<ScreenConfig, 'generatedResults' | 'selectedResultId' | 'status'>> = {
  'splash': {
    id: 'splash',
    name: 'Splash Screen',
    description: 'Game loading/intro screen with logo, tagline, and loading bar',
    userPrompt: '',
    referenceImages: [],
  },
  'main-screen': {
    id: 'main-screen',
    name: 'Main Screen',
    description: 'Main gameplay screen with reel grid, symbols, spin button, and bet controls',
    userPrompt: '',
    referenceImages: [],
  },
  'mega-win': {
    id: 'mega-win',
    name: 'Mega Win',
    description: 'Big win celebration with animated text, coin effects, and multiplier display',
    userPrompt: '',
    referenceImages: [],
  },
  'free-spin-trigger': {
    id: 'free-spin-trigger',
    name: 'Free Spin Trigger',
    description: 'Transition screen when scatter symbols trigger free spins',
    userPrompt: '',
    referenceImages: [],
  },
  'free-spin-process': {
    id: 'free-spin-process',
    name: 'Free Spin Process',
    description: 'Free spins gameplay with enhanced grid, multiplier, and remaining spin counter',
    userPrompt: '',
    referenceImages: [],
  },
  'free-spin-end': {
    id: 'free-spin-end',
    name: 'Free Spin End',
    description: 'Free spins summary with total win amount and return-to-main transition',
    userPrompt: '',
    referenceImages: [],
  },
};

export const DEFAULT_VIDEO_CONFIG: VideoCompilationConfig = {
  screenDurations: {
    'splash': 3,
    'main-screen': 5,
    'mega-win': 4,
    'free-spin-trigger': 3,
    'free-spin-process': 8,
    'free-spin-end': 4,
  },
  width: 720,
  height: 1280,
  fps: 30,
  format: 'mp4',
  videoModel: 'veo',
  totalDuration: 30,
  resolutionPreset: '9:16',
};

export const VIDEO_RESOLUTION_PRESETS: Record<VideoResolutionPreset, { width: number; height: number } | null> = {
  '16:9': { width: 1280, height: 720 },
  '9:16': { width: 720, height: 1280 },
  'custom': null,
};

export const VIDEO_DURATION_RANGE = { min: 5, max: 120, step: 5, default: 30 };

// ─── Per-Screen Animation System Prompts ─────────────────
// Global rules prepended to every screen's system prompt.
const GLOBAL_ANIMATION_RULES = `=== GLOBAL RULES (APPLY TO ALL SCREENS) ===

## REFERENCE IMAGE IS THE ONLY SOURCE OF TRUTH
- All visual information must be extracted directly from the reference images provided for each screen.
- Do NOT invent, assume, redesign, or replace any element that is not visible in the reference.
- If an element is unclear in the reference, keep it simple/blurry rather than redesigning it.
- Every frame must visually match its reference image.

## GLOBAL STYLE LOCK
- Detect the art style from the reference images and lock it across ALL screens and ALL frames.
- Do not switch art styles between screens.
- Transitions between screens must not alter the established art style.
- Less detail is always better than wrong detail.

## GLOBAL UI CONSISTENCY
- All fonts, colors, button shapes, and layout positions must remain consistent across screens unless the reference shows a deliberate change.
- English language must be used for all UI text labels throughout the entire video.
- Do not add any UI element not seen in the reference.

## GLOBAL ANIMATION RULES
- Frame rate: 30fps throughout all screens.
- Aspect ratio: 9:16 portrait, maintained always.
- All transitions between screens must be smooth — no abrupt cuts unless reference shows a cut.
- Acceptable transitions: fade, flash, slide, burst.

## GLOBAL DURATION RULE
- Every screen must be exactly 8 seconds long.
- This includes the transition out at the end of each screen — it is part of the 8 seconds.
- Do not shorten or extend any screen beyond 8 seconds under any circumstance.`;

export const DEFAULT_ANIMATION_SYSTEM_PROMPTS: Record<SlotScreenId, string> = {
  'splash': `${GLOBAL_ANIMATION_RULES}

=== SCREEN — LOADING SCREEN (DURATION: EXACTLY 8 SECONDS) ===

## PURPOSE
Opens the video. Builds anticipation before the main game is revealed.

## VISUAL RULES
- Lock background art, logo position, and color palette exactly from loading screen reference.
- Progress bar style, color, shape, and position must match reference exactly.
- Any mascot or character visible must only do subtle idle animation: breathing or blinking.
- Game logo must remain static or do a simple pulse/glow effect only.
- Do not add sparkles or particles not in reference.

## ANIMATION SEQUENCE (8 seconds total)
- 0:00 - 0:05 : Screen appears — logo and background fully visible.
- 0:05 - 7:00 : Progress bar fills smoothly from 0% to 100%. Idle animation plays on character.
- 7:00 - 7:30 : Progress bar reaches 100%. Brief pause on completed state.
- 7:30 - 8:00 : Transition out begins — fade or slide to next screen.

## TRANSITION OUT
Fade or slide into Main Game Screen.`,

  'main-screen': `${GLOBAL_ANIMATION_RULES}

=== SCREEN — MAIN GAME SCREEN (DURATION: EXACTLY 8 SECONDS) ===

## PURPOSE
The base game screen. Shows two spins playing out. The second spin lands a Mega Win combination which triggers the Mega Win celebration screen.

## VISUAL RULES
- Reel grid structure must match reference exactly — same number of columns and rows.
- Every symbol visible in reference must be locked: same colors, same proportions, same art style.
- Wooden/metal frame border around grid must be identical to reference in every frame.
- Background must match reference and remain consistent throughout all 8 seconds.

## HUD RULES (lock from reference)
- Coin balance display on the left.
- Center action button label in English.
- Bet amount display on the right.
- Footer buttons: spin options, spin button, bet amount, settings gear.
- All positions and styles locked from reference.

## ANIMATION SEQUENCE (8 seconds total)
- 0:00 - 0:30 : Main game screen appears after loading transition. Reels settle into idle position.
--- SPIN 1 (normal spin, no win) ---
- 0:30 - 1:30 : Player initiates first spin — reels begin spinning vertically top to bottom.
- 1:30 - 2:30 : Reels stop column by column from left to right. No winning combination lands. Brief idle pause after stop.
--- SPIN 2 (winning spin → Mega Win) ---
- 2:30 - 3:30 : Player initiates second spin — reels begin spinning vertically top to bottom with slightly more energy than Spin 1.
- 3:30 - 5:00 : Reels stop column by column from left to right. A high-value winning combination lands on the grid.
- 5:00 - 6:30 : Winning symbols flash and glow intensely on the grid. Winning lines highlight across the reels. Coin balance begins to tick up.
- 6:30 - 7:30 : Glow and flash effects peak — screen begins to build up to Mega Win explosion.
- 7:30 - 8:00 : Transition out begins — massive flash or burst effect exploding into next screen.

## TRANSITION OUT
Massive flash or explosion burst into Mega Win Screen.

## IMPORTANT NOTE
- Spin 1 must look like a normal uneventful spin.
- Spin 2 must build tension and land the win.
- The transition to Mega Win must feel earned and explosive — not sudden or random.`,

  'mega-win': `${GLOBAL_ANIMATION_RULES}

=== SCREEN — MEGA WIN SCREEN (DURATION: EXACTLY 8 SECONDS) ===

## PURPOSE
Celebrates the large win achieved on Spin 2 of the main game with a dramatic display. This happens BEFORE Free Spins are triggered.

## VISUAL RULES
- WIN TEXT style must match the font and color in reference exactly.
- Text must be in English: "MEGA WIN"
- Coin or particle explosion effects must follow the same color palette as reference.
- Win amount number display style must be locked from reference: font, size, color.
- Any character celebration pose must stay consistent with the art style in reference.
- Background flash or burst must not overpower the main UI elements.
- Do not add effects not visible in reference.

## ANIMATION SEQUENCE (8 seconds total)
- 0:00 - 1:00 : Screen explodes in from Main Game Screen flash.
- 1:00 - 2:00 : "MEGA WIN" text appears with impact — zoom-in or pulse effect.
- 2:00 - 3:00 : Coins or gems begin showering down continuously from top.
- 3:00 - 6:00 : Win amount counter rolls upward numerically from 0 to final value. Coins continue showering. Characters celebrate if in reference.
- 6:00 - 7:30 : Screen holds on final win amount. Celebration particles loop.
- 7:30 - 8:00 : Transition out begins — fade or slide to next screen.

## TRANSITION OUT
Fade or slide into Free Spin Trigger Screen.`,

  'free-spin-trigger': `${GLOBAL_ANIMATION_RULES}

=== SCREEN — FREE SPIN TRIGGER (DURATION: EXACTLY 8 SECONDS) ===

## PURPOSE
After the Mega Win celebration ends, the game reveals that SCATTER symbols have also landed, triggering Free Spins as a bonus on top of the Mega Win.

## VISUAL RULES
- SCATTER symbols must match reference exactly — same design, position, and glow color.
- Trigger announcement text style must be locked from reference: font, color, and size.
- Free spin count number must be clearly visible in the style shown in reference.
- Background dimming effect (if in reference) must be replicated exactly.
- Energy burst or highlight colors must match the palette seen in reference only.
- All announcement text must be in English.

## ANIMATION SEQUENCE (8 seconds total)
- 0:00 - 1:00 : Screen transitions in from Mega Win — background dims slightly (if shown in reference). Reel grid reappears briefly.
- 1:00 - 3:00 : SCATTER symbols on the grid light up one by one in sequence with growing intensity.
- 3:00 - 5:00 : All SCATTERs fully glowing — burst effect plays across screen.
- 5:00 - 6:30 : Free spin count number is revealed on screen with impact. Text example: "15 FREE SPINS"
- 6:30 - 7:30 : Screen holds on free spin number — subtle pulse effect.
- 7:30 - 8:00 : Transition out begins — slide or flash to next screen.

## TRANSITION OUT
Slide or flash into Free Spin Process Screen.`,

  'free-spin-process': `${GLOBAL_ANIMATION_RULES}

=== SCREEN — FREE SPIN PROCESS (DURATION: EXACTLY 8 SECONDS) ===

## PURPOSE
Shows the actual free spins being played out. Visually distinct from base game to signal the special mode is active.

## VISUAL RULES
- All reel symbols must match reference exactly — same design, no redesign allowed.
- Free spin counter (remaining spins) must be visible and match position from reference.
- If reel frame or background changes color/glow in free spin mode, replicate from reference only.
- Any special multiplier or wild symbols must follow the visual style in reference.
- Winning line highlights must use the same color and animation style as reference.
- Free spin counter label must be in English: "FREE SPINS REMAINING"
- HUD changes during free spin mode (if any) must be locked from reference.

## ANIMATION SEQUENCE (8 seconds total)
- 0:00 - 1:00 : Free spin mode activates — reel frame and background shift to free spin style from reference. Counter shows starting number.
- 1:00 - 3:00 : First spin plays — reels spin vertically and stop. Counter decreases by 1. Winning lines highlight if any.
- 3:00 - 5:00 : Second spin plays — reels spin vertically and stop. Counter decreases by 1. Winning lines highlight if any.
- 5:00 - 7:00 : Third spin plays — reels spin vertically and stop. Counter decreases by 1. Special symbols glow if present.
- 7:00 - 7:30 : Counter reaches 0. Screen briefly holds.
- 7:30 - 8:00 : Transition out begins — flash or burst to next screen.

## TRANSITION OUT
Flash or burst into Total Win Screen.`,

  'free-spin-end': `${GLOBAL_ANIMATION_RULES}

=== SCREEN — TOTAL WIN SCREEN (DURATION: EXACTLY 8 SECONDS) ===

## PURPOSE
Final summary screen. Shows the combined total winnings from both the Mega Win and all Free Spins before returning to the base game.

## VISUAL RULES
- Total win amount display must match the font, color, and size from reference exactly.
- Background celebration effect (coins, confetti, burst) must replicate the style in reference.
- Any character or mascot celebrating must stay consistent with the art style in reference.
- Summary text label must be in English: "TOTAL WIN"
- Return to base game button or transition element must match the UI style in reference.
- Do not add celebration effects not in reference.

## ANIMATION SEQUENCE (8 seconds total)
- 0:00 - 1:00 : Screen transitions in from Free Spin Process screen. "TOTAL WIN" label appears.
- 1:00 - 4:00 : Total win amount rolls up numerically from 0 to final value. This total reflects Mega Win plus all Free Spin winnings. Coins or particles loop in background during count-up.
- 4:00 - 6:30 : Screen holds on final total. Celebration particles continue. Characters hold celebrate pose.
- 6:30 - 7:30 : Return to base game prompt or button appears (from reference).
- 7:30 - 8:00 : Transition out begins — fade back to Main Game Screen.

## TRANSITION OUT
Fade back to Main Game Screen to complete loop.`,
};

export function buildInitialVideoSlotImages(): Record<SlotScreenId, VideoSlotImage> {
  const result = {} as Record<SlotScreenId, VideoSlotImage>;
  for (const id of SLOT_SCREEN_ORDER) {
    result[id] = { screenId: id, imageUrl: null, source: 'generated', animationPrompt: '', animationSystemPrompt: DEFAULT_ANIMATION_SYSTEM_PROMPTS[id] };
  }
  return result;
}

export const DEFAULT_GENERATION_SETTINGS: ScreenGenerationSettings = {
  model: 'nano-banana-2',
  imageCount: 1,
};

export const SCREEN_ICONS: Record<SlotScreenId, string> = {
  'splash': 'Image',
  'main-screen': 'Gamepad2',
  'mega-win': 'Trophy',
  'free-spin-trigger': 'Target',
  'free-spin-process': 'Dices',
  'free-spin-end': 'Flag',
};
